import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Navbar from "../components/Navbar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Clock, AlertTriangle, ChevronLeft, ChevronRight, CheckCircle2,
  XCircle, Award, RefreshCw, Home, Target
} from "lucide-react";
import { toast } from "sonner";
import MockTestReview from "../components/exam/MockTestReview";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MockTestSession() {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const token = localStorage.getItem("token");

  const [test, setTest] = useState(null);
  const [answers, setAnswers] = useState({}); // { questionIndex: 'a' | 'b' | ... }
  const [currentIdx, setCurrentIdx] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const autoSubmittedRef = useRef(false);

  useEffect(() => {
    fetchTest();
  }, [sessionId]);

  // Timer
  useEffect(() => {
    if (!test || result) return;
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          if (!autoSubmittedRef.current) {
            autoSubmittedRef.current = true;
            toast.warning("Time up! Auto-submitting your answers...");
            submitTest();
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [test, result]);

  const fetchTest = async () => {
    try {
      const res = await axios.get(`${API}/exam-prep/mock-test/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTest(res.data);
      // Calculate remaining time from auto_submit_at
      const autoSubmit = new Date(res.data.auto_submit_at).getTime();
      const now = Date.now();
      const secondsLeft = Math.max(0, Math.floor((autoSubmit - now) / 1000));
      setTimeLeft(secondsLeft);
      // If already completed
      if (res.data.completed) {
        setResult({
          session_id: res.data.id,
          total_questions: res.data.total_questions,
          correct: res.data.correct_count,
          incorrect: res.data.incorrect_count,
          skipped: res.data.skipped_count,
          raw_score: res.data.raw_score,
          accuracy_percent: res.data.total_questions
            ? Math.round((res.data.correct_count / res.data.total_questions) * 100 * 10) / 10
            : 0,
          section_scores: res.data.section_scores,
          weak_topics: res.data.weak_topics || [],
          strong_topics: res.data.strong_topics || [],
        });
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load mock test");
      navigate("/exam-prep");
    }
  };

  const selectAnswer = (idx, opt) => {
    setAnswers({ ...answers, [idx]: opt });
  };

  const submitTest = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/exam-prep/mock-test/submit`,
        { session_id: sessionId, answers },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(res.data);
      toast.success("Mock test submitted!");
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Failed to submit test");
    } finally {
      setSubmitting(false);
    }
  };

  const confirmSubmit = () => {
    const answered = Object.keys(answers).length;
    const total = test.questions.length;
    if (!window.confirm(`Submit test?\n\n${answered}/${total} answered\n${total - answered} skipped`)) return;
    submitTest();
  };

  if (!test) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <Navbar />
        <div className="flex items-center justify-center py-24">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  // Result view
  if (result) {
    const reviewAnswers = Object.keys(answers).length ? answers : test.answers || {};
    return (
      <MockTestResult
        test={test}
        result={result}
        navigate={navigate}
        userAnswers={reviewAnswers}
        sessionId={sessionId}
      />
    );
  }

  const currentQ = test.questions[currentIdx];
  const answered = Object.keys(answers).length;
  const currentSection = test.sections?.find(
    (s) => currentIdx >= s.start_index && currentIdx <= s.end_index
  );

  // Time formatting
  const fmtTime = (s) => {
    const hh = String(Math.floor(s / 3600)).padStart(2, "0");
    const mm = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
    const ss = String(s % 60).padStart(2, "0");
    return `${hh}:${mm}:${ss}`;
  };
  const lowTime = timeLeft <= 300; // 5 min warning

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <Navbar />
      <div className="py-4 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Sticky Header */}
        <Card className="mb-4 border-0 shadow-md sticky top-4 z-10" data-testid="mock-test-header">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="font-bold text-gray-900">{test.exam_name}</p>
                {currentSection && (
                  <p className="text-xs text-gray-600">
                    {currentSection.name} • Q{currentIdx + 1}/{test.total_questions}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3">
                <Badge
                  className={`text-sm px-3 py-1 ${
                    lowTime ? "bg-red-100 text-red-800 animate-pulse" : "bg-blue-100 text-blue-800"
                  }`}
                  data-testid="mock-test-timer"
                >
                  <Clock className="w-3 h-3 mr-1 inline" /> {fmtTime(timeLeft)}
                </Badge>
                <Badge variant="outline" data-testid="mock-answered-count">
                  {answered}/{test.total_questions} answered
                </Badge>
                <Button
                  onClick={confirmSubmit}
                  disabled={submitting}
                  size="sm"
                  className="bg-gradient-to-r from-green-600 to-emerald-600"
                  data-testid="submit-mock-btn"
                >
                  {submitting ? (
                    <RefreshCw className="w-3 h-3 animate-spin" />
                  ) : (
                    "Submit Test"
                  )}
                </Button>
              </div>
            </div>
            <Progress
              value={(answered / test.total_questions) * 100}
              className="h-1.5 mt-2"
            />
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Question Area */}
          <div className="lg:col-span-3">
            <Card className="border-0 shadow-lg" data-testid="mock-question-card">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <Badge variant="outline">Q{currentIdx + 1}</Badge>
                  {currentSection && (
                    <Badge className="bg-blue-100 text-blue-800 text-xs">
                      {currentSection.short_name}
                    </Badge>
                  )}
                </div>
                <p className="text-lg font-medium text-gray-900 mb-4 leading-relaxed">
                  {currentQ.question}
                </p>
                <div className="space-y-2">
                  {["a", "b", "c", "d"].map((opt) => (
                    <button
                      key={opt}
                      onClick={() => selectAnswer(currentIdx, opt)}
                      className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                        answers[currentIdx] === opt
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 bg-white hover:border-blue-300"
                      }`}
                      data-testid={`mock-option-${opt}`}
                    >
                      <div className="flex items-start gap-3">
                        <span
                          className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                            answers[currentIdx] === opt
                              ? "bg-blue-500 text-white"
                              : "bg-gray-200 text-gray-700"
                          }`}
                        >
                          {opt.toUpperCase()}
                        </span>
                        <span className="flex-1 text-sm text-gray-800">
                          {currentQ.options?.[opt]}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>

                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentIdx(Math.max(0, currentIdx - 1))}
                    disabled={currentIdx === 0}
                    data-testid="prev-question-btn"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" /> Previous
                  </Button>
                  <Button
                    onClick={() => setAnswers({ ...answers, [currentIdx]: undefined })}
                    variant="ghost"
                    size="sm"
                    disabled={!answers[currentIdx]}
                    data-testid="clear-answer-btn"
                  >
                    Clear
                  </Button>
                  <Button
                    onClick={() =>
                      setCurrentIdx(Math.min(test.total_questions - 1, currentIdx + 1))
                    }
                    disabled={currentIdx === test.total_questions - 1}
                    data-testid="next-question-btn"
                  >
                    Next <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Question Navigator */}
          <div className="lg:col-span-1">
            <Card className="border-0 shadow-md">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Question Navigator</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-5 lg:grid-cols-4 gap-1.5">
                  {test.questions.map((_, i) => {
                    const isAnswered = answers[i] !== undefined && answers[i] !== null;
                    const isCurrent = i === currentIdx;
                    return (
                      <button
                        key={i}
                        onClick={() => setCurrentIdx(i)}
                        className={`w-9 h-9 rounded-md text-xs font-semibold transition-all ${
                          isCurrent
                            ? "bg-blue-600 text-white ring-2 ring-blue-300"
                            : isAnswered
                            ? "bg-green-500 text-white"
                            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                        }`}
                        data-testid={`nav-q-${i + 1}`}
                      >
                        {i + 1}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-3 text-xs space-y-1">
                  <div className="flex items-center gap-1.5">
                    <span className="inline-block w-3 h-3 bg-green-500 rounded" />
                    Answered ({answered})
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="inline-block w-3 h-3 bg-gray-300 rounded" />
                    Not answered ({test.total_questions - answered})
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {lowTime && (
          <Card className="mt-4 border-0 shadow-md bg-red-50 border-l-4 border-l-red-500">
            <CardContent className="pt-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <p className="text-sm text-red-800 font-semibold">
                Less than 5 minutes remaining! Please review your answers and submit.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
      </div>
    </div>
  );
}

const MockTestResult = ({ test, result, navigate, userAnswers, sessionId }) => (
  <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
    <Navbar />
    <div className="py-8 px-4">
    <div className="max-w-3xl mx-auto">
      <Card className="border-0 shadow-xl" data-testid="mock-result-card">
        <CardHeader className="text-center">
          <div className="mx-auto p-4 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full w-fit mb-3">
            <Award className="w-10 h-10 text-white" />
          </div>
          <CardTitle className="text-2xl">Mock Test Complete!</CardTitle>
          <CardDescription>{test.exam_name}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Overall Score */}
          <div className="text-center p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl">
            <p className="text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              {result.accuracy_percent}%
            </p>
            <p className="text-gray-600 mt-1">Accuracy</p>
            <p className="text-xs text-gray-500 mt-2">
              Raw Score: {result.raw_score?.toFixed?.(1) || result.raw_score} marks
            </p>
          </div>

          {/* Breakdown */}
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-600">{result.correct}</p>
              <p className="text-xs text-gray-600">Correct</p>
            </div>
            <div className="text-center p-3 bg-red-50 rounded-lg">
              <p className="text-2xl font-bold text-red-600">{result.incorrect}</p>
              <p className="text-xs text-gray-600">Incorrect</p>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-gray-600">{result.skipped}</p>
              <p className="text-xs text-gray-600">Skipped</p>
            </div>
          </div>

          {/* Section Scores */}
          {result.section_scores && (
            <div>
              <p className="font-semibold text-sm mb-2">Section-wise Performance:</p>
              <div className="space-y-1.5">
                {Object.entries(result.section_scores).map(([name, s]) => (
                  <div key={name} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                    <span>{name}</span>
                    <Badge className="bg-blue-100 text-blue-800">
                      {s.correct}/{s.total}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Weak Topics */}
          {result.weak_topics?.length > 0 && (
            <div>
              <p className="font-semibold text-sm mb-2 text-red-700">Topics to Improve:</p>
              <div className="flex flex-wrap gap-1">
                {result.weak_topics.map((t, i) => (
                  <Badge key={i} className="bg-red-100 text-red-800 text-xs">{t}</Badge>
                ))}
              </div>
            </div>
          )}

          {/* Strong Topics */}
          {result.strong_topics?.length > 0 && (
            <div>
              <p className="font-semibold text-sm mb-2 text-green-700">Strong Areas:</p>
              <div className="flex flex-wrap gap-1">
                {result.strong_topics.map((t, i) => (
                  <Badge key={i} className="bg-green-100 text-green-800 text-xs">{t}</Badge>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 pt-2">
            <Button variant="outline" onClick={() => navigate(`/exam-prep/${test.exam_id}`)}>
              <Target className="w-4 h-4 mr-1" /> Practice Weak Topics
            </Button>
            <Button
              onClick={() => navigate("/exam-prep")}
              className="bg-gradient-to-r from-blue-600 to-indigo-600"
            >
              <Home className="w-4 h-4 mr-1" /> Exam Hub
            </Button>
          </div>
        </CardContent>
      </Card>

      <MockTestReview test={test} userAnswers={userAnswers} sessionId={sessionId} />
    </div>
    </div>
  </div>
);
